<template>
  <div class="pa-4">
    <!-- Header -->
    <div class="d-flex align-center mb-6">
      <v-btn icon="mdi-arrow-left" variant="text" @click="$router.back()" class="mr-2"></v-btn>
      <div>
        <h1 class="text-h5 font-weight-black">Pinned Announcements</h1>
        <p class="text-caption text-grey mt-1">Announcements shown at the top of the home feed</p>
      </div>
      <v-spacer></v-spacer>
      <v-btn
        color="primary"
        variant="flat"
        prepend-icon="mdi-plus"
        class="rounded-pill font-weight-bold"
        @click="openCreate"
      >
        New Announcement
      </v-btn>
    </div>

    <!-- Access denied -->
    <v-alert v-if="!isAdminUser" type="error" variant="tonal" class="rounded-xl border mb-4">
      Access denied. This page is only accessible to the admin account.
    </v-alert>

    <template v-else>
      <!-- Loading -->
      <div v-if="loading" class="text-center py-12">
        <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
        <p class="mt-4 text-grey">Loading announcements...</p>
      </div>

      <!-- Error -->
      <v-alert v-else-if="error" type="error" variant="tonal" class="rounded-xl border mb-4">
        {{ error }}
      </v-alert>

      <!-- Empty -->
      <div
        v-else-if="items.length === 0"
        class="text-center py-12 border rounded-xl border-dashed"
        style="border-color: rgba(255,255,255,0.1) !important;"
      >
        <v-icon icon="mdi-pin-outline" size="64" color="grey-darken-2" class="mb-4"></v-icon>
        <h3 class="text-h6 text-grey">No announcements yet</h3>
        <p class="text-body-2 text-grey-darken-1 mt-2">Create one to pin it to the home feed.</p>
      </div>

      <!-- List -->
      <div v-else class="d-flex flex-column ga-3">
        <!-- Preview of what it looks like -->
        <div class="text-overline text-grey mb-1">Preview</div>
        <template v-for="item in items" :key="item.rkey">
          <v-alert
            :type="item.type"
            variant="tonal"
            class="rounded-xl border mb-1"
            :style="!item.active ? 'opacity: 0.4;' : ''"
          >
            <div class="font-weight-black mb-1">{{ item.title }}</div>
            <div class="text-body-2" style="white-space: pre-wrap;">{{ item.message }}</div>
          </v-alert>

          <!-- Metadata row -->
          <div class="d-flex align-center ga-2 mb-3 px-1">
            <v-chip
              :color="item.active ? 'success' : 'grey'"
              size="x-small"
              variant="tonal"
              :prepend-icon="item.active ? 'mdi-eye' : 'mdi-eye-off'"
              class="cursor-pointer"
              @click="toggleActive(item)"
            >
              {{ item.active ? 'Active' : 'Inactive' }}
            </v-chip>
            <v-chip size="x-small" color="grey" variant="tonal">
              {{ item.type }}
            </v-chip>
            <span class="text-caption text-grey" style="opacity: 0.5;">
              {{ formatDate(item.createdAt) }}
            </span>
            <v-spacer></v-spacer>
            <v-btn icon="mdi-pencil" size="x-small" variant="text" color="grey" @click="openEdit(item)"></v-btn>
            <v-btn icon="mdi-delete" size="x-small" variant="text" color="error" @click="confirmDelete(item)"></v-btn>
          </div>
        </template>
      </div>
    </template>

    <!-- Create / Edit Dialog -->
    <v-dialog v-model="showForm" max-width="560px" scrollable>
      <v-card class="rounded-xl bg-surface border" style="border-color: rgba(255,255,255,0.08) !important;">
        <v-card-title class="font-weight-black pa-5 pb-0 d-flex align-center justify-space-between">
          {{ editingItem ? 'Edit Announcement' : 'New Announcement' }}
          <v-btn icon="mdi-close" variant="text" size="small" @click="showForm = false"></v-btn>
        </v-card-title>

        <v-card-text class="pa-5">
          <v-form ref="formRef">
            <!-- Live preview -->
            <v-alert
              :type="form.type"
              variant="tonal"
              class="rounded-xl mb-5"
            >
              <div class="font-weight-black mb-1">{{ form.title || 'Title preview' }}</div>
              <div class="text-body-2" style="white-space: pre-wrap;">{{ form.message || 'Message preview…' }}</div>
            </v-alert>

            <v-select
              v-model="form.type"
              :items="typeOptions"
              item-title="label"
              item-value="value"
              label="Type"
              variant="outlined"
              density="comfortable"
              class="mb-3 rounded-lg"
            ></v-select>

            <v-text-field
              v-model="form.title"
              label="Title *"
              variant="outlined"
              density="comfortable"
              class="mb-3 rounded-lg"
              :rules="[v => !!v || 'Title is required']"
            ></v-text-field>

            <v-textarea
              v-model="form.message"
              label="Message *"
              variant="outlined"
              density="comfortable"
              class="mb-3 rounded-lg"
              rows="4"
              auto-grow
              :rules="[v => !!v || 'Message is required']"
            ></v-textarea>

            <v-switch
              v-model="form.active"
              label="Active (visible on home feed)"
              color="primary"
              density="comfortable"
              hide-details
            ></v-switch>
          </v-form>
        </v-card-text>

        <v-card-actions class="pa-5 pt-0">
          <v-spacer></v-spacer>
          <v-btn variant="text" class="font-weight-bold" @click="showForm = false">Cancel</v-btn>
          <v-btn
            color="primary"
            variant="flat"
            class="rounded-pill font-weight-bold px-6"
            :loading="saving"
            @click="submitForm"
          >
            {{ editingItem ? 'Save Changes' : 'Publish' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete confirmation -->
    <v-dialog v-model="showDeleteConfirm" max-width="420px">
      <v-card class="rounded-xl bg-surface border" style="border-color: rgba(255,255,255,0.08) !important;">
        <v-card-title class="text-h6 font-weight-black pa-6">Delete Announcement?</v-card-title>
        <v-card-text class="pa-6 pt-0 text-grey-lighten-1">
          "<strong>{{ itemToDelete?.title }}</strong>" will be permanently removed.
        </v-card-text>
        <v-card-actions class="pa-6 pt-0">
          <v-spacer></v-spacer>
          <v-btn variant="text" class="font-weight-bold" @click="showDeleteConfirm = false">Cancel</v-btn>
          <v-btn color="error" variant="flat" class="rounded-pill font-weight-bold px-6" :loading="deleting" @click="doDelete">
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" rounded="pill" location="bottom">
      {{ snackbar.text }}
    </v-snackbar>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import {
  isAdmin,
  getPinnedInfos,
  createPinnedInfo,
  updatePinnedInfo,
  deletePinnedInfo,
  type PinnedInfo
} from '@/services/bluesky';

const isAdminUser = computed(() => isAdmin());

const items = ref<PinnedInfo[]>([]);
const loading = ref(true);
const error = ref('');

const showForm = ref(false);
const editingItem = ref<PinnedInfo | null>(null);
const saving = ref(false);
const formRef = ref<any>(null);

const showDeleteConfirm = ref(false);
const itemToDelete = ref<PinnedInfo | null>(null);
const deleting = ref(false);

const snackbar = ref({ show: false, text: '', color: 'success' });

const typeOptions = [
  { label: 'Info', value: 'info' },
  { label: 'Warning', value: 'warning' },
  { label: 'Success', value: 'success' },
  { label: 'Error', value: 'error' }
];

const emptyForm = (): Omit<PinnedInfo, 'uri' | 'cid' | 'rkey' | 'createdAt'> => ({
  title: '',
  message: '',
  type: 'info',
  active: true
});

const form = ref(emptyForm());

const notify = (text: string, color = 'success') => {
  snackbar.value = { show: true, text, color };
};

const formatDate = (iso: string) => {
  try { return new Date(iso).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' }); }
  catch { return iso; }
};

const load = async () => {
  loading.value = true;
  error.value = '';
  try {
    items.value = await getPinnedInfos();
  } catch (e: any) {
    error.value = e?.message || 'Failed to load';
  } finally {
    loading.value = false;
  }
};

const openCreate = () => {
  editingItem.value = null;
  form.value = emptyForm();
  showForm.value = true;
};

const openEdit = (item: PinnedInfo) => {
  editingItem.value = item;
  form.value = {
    title: item.title,
    message: item.message,
    type: item.type,
    active: item.active
  };
  showForm.value = true;
};

const submitForm = async () => {
  const { valid } = await formRef.value?.validate();
  if (!valid) return;
  saving.value = true;
  try {
    if (editingItem.value?.rkey) {
      await updatePinnedInfo(editingItem.value.rkey, form.value);
      notify('Announcement updated');
    } else {
      await createPinnedInfo(form.value);
      notify('Announcement published');
    }
    showForm.value = false;
    await load();
  } catch (e: any) {
    notify(e?.message || 'Failed to save', 'error');
  } finally {
    saving.value = false;
  }
};

const toggleActive = async (item: PinnedInfo) => {
  try {
    await updatePinnedInfo(item.rkey!, { ...item, active: !item.active });
    await load();
  } catch (e: any) {
    notify(e?.message || 'Failed to update', 'error');
  }
};

const confirmDelete = (item: PinnedInfo) => {
  itemToDelete.value = item;
  showDeleteConfirm.value = true;
};

const doDelete = async () => {
  if (!itemToDelete.value?.rkey) return;
  deleting.value = true;
  try {
    await deletePinnedInfo(itemToDelete.value.rkey);
    notify('Announcement deleted');
    showDeleteConfirm.value = false;
    await load();
  } catch (e: any) {
    notify(e?.message || 'Failed to delete', 'error');
  } finally {
    deleting.value = false;
  }
};

onMounted(() => {
  if (isAdminUser.value) load();
  else loading.value = false;
});
</script>
